import type { FC, ReactElement, ReactNode } from 'react';
import { createContext, forwardRef, useContext, useEffect, useRef, useState } from 'react';

import Motion from '@snowit/ui/utils/Motion';
import executer from '@snowit/utils/executer';
import randomId from '@snowit/utils/randomId';

import useDomElement from './useDomElement';

import { AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';

const Context = createContext({});

export interface IPortal {
  content: ReactNode | ((...args: any[]) => ReactNode);
  clickToHide?: boolean;
  escToHide?: boolean;
  mounted: boolean;
  bindTo?: Element | ReactNode;
  onClose: () => void;
  className?: string;
  children?: ReactNode;
  id?: string;
  position?: 'adaptive' | string;
  fullscreen?: boolean;
  onMount?: () => void;
  onUnmount?: () => void;
}

/** Gap kept between an adaptive portal and the viewport edge. */
const VIEWPORT_MARGIN = 8;

/**
 * Height the portal content needs. The content can be collapsed by an opening
 * animation (height 0), so `scrollHeight` is used. Its direct children are
 * summed too: a scrollable child (e.g. a list in a `1fr` grid row) shrinks with
 * its parent and hides its height from the parent's `scrollHeight`.
 * Returns 0 when it cannot be measured.
 */
const getContentHeight = (portal: HTMLElement) => {
  const content = portal.firstElementChild;
  if (!(content instanceof HTMLElement)) return 0;
  const children = Array.from(content.children).reduce((sum, child) => sum + child.scrollHeight, 0);
  return Math.max(content.scrollHeight, children);
};

/**
 * Get portal element position based on "position" prop
 * prop can be:
 * "adaptive" - open below the parent when the content fits, otherwise on the
 *   side with more room; `available` is the room left on the chosen side
 * "<X position> [<Y position>]" e.g. "left bottom" or "right top"
 * if no position specified, "left bottom" is used
 */
const getPosition = (portal, container, props) => {
  let x = 0;
  let y = 0;
  let xParam = 'left';
  let yParam = 'top';
  let available: number | undefined;
  const cnt = container.getBoundingClientRect();
  const prt = portal.getBoundingClientRect();
  const doc = document.documentElement || document.body;
  const { innerWidth, innerHeight } = window;
  const { clientWidth, clientHeight } = doc;
  const width = clientWidth || innerWidth;
  const height = clientHeight || innerHeight;
  const position = props.position || 'left bottom';
  if (position === 'adaptive') {
    const spaceBelow = Math.max(0, height - (cnt.y + cnt.height) - VIEWPORT_MARGIN);
    const spaceAbove = Math.max(0, cnt.y - VIEWPORT_MARGIN);
    const needed = getContentHeight(portal);
    const fitsBelow = needed > 0 && needed <= spaceBelow;
    const vertical = !fitsBelow && spaceAbove > spaceBelow;
    yParam = vertical ? 'bottom' : 'top';
    const bottom = height - cnt.y;
    const top = cnt.y + cnt.height;
    x = cnt.x + prt.width > width - 10 ? cnt.x + cnt.width - prt.width : cnt.x;
    y = vertical ? bottom : top;
    available = vertical ? spaceAbove : spaceBelow;
  } else {
    const [xPos, yPos] = position.split(' ');
    switch (xPos) {
      case 'right':
        x = width - (cnt.x + cnt.width);
        xParam = 'right';
        break;
      case 'center':
        x = cnt.x - prt.width / 2;
        break;
      case 'left':
      default:
        x = cnt.x;
        break;
    }

    switch (yPos) {
      case 'top':
        y = height - cnt.y;
        yParam = 'bottom';
        break;
      case 'center':
        y = cnt.y - prt.height / 2;
        break;
      case 'bottom':
      default:
        y = cnt.y + cnt.height;
        break;
    }
  }
  return { x, y, xParam, yParam, available };
};

export const usePortals = () => useContext(Context);

const InnerPortal = forwardRef((props: IPortal, ref: any) => {
  const { content, bindTo, fullscreen } = props;
  const { children, className } = props;

  const handlePortalPosition = () => {
    if (!ref?.current) return;
    const zIndex = 9999;
    ref.current.style.position = 'fixed';
    ref.current.style.zIndex = zIndex;
    if (fullscreen) return;
    if (bindTo === document.body) {
      // ref.current.style.top = '50%';
      // ref.current.style.left = '50%';
      // ref.current.style.transform = 'translate(-50%,-50%)';
      //
    } else {
      // const { x, y, height } = bindTo.getBoundingClientRect();
      // const aa = ref.current.getBoundingClientRect();
      // const bb = bindTo.getBoundingClientRect();
      const { x, y, xParam, yParam, available } = getPosition(ref.current, bindTo, props);
      const { style } = ref.current;
      // the side can change between calls: clear the previous anchoring
      style.top = '';
      style.bottom = '';
      style.left = '';
      style.right = '';
      style[yParam] = `${y}px`;
      style[xParam] = `${x}px`;
      // lets the content cap its height and scroll instead of overflowing
      if (available === undefined) style.removeProperty('--portal-available-h');
      else style.setProperty('--portal-available-h', `${available}px`);
    }
  };

  useEffect(() => {
    if (fullscreen) {
      ref.current.style.top = 0;
      ref.current.style.right = 0;
      ref.current.style.bottom = 0;
      ref.current.style.left = 0;
    }
  }, [fullscreen]);

  useEffect(() => {
    if (!bindTo || !ref.current) return () => null;
    handlePortalPosition();
    if (bindTo === document.body || fullscreen) {
      // document.body.style.position = 'fixed';
      document.body.style.overflowY = 'hidden';
      // document.body.style.inset = 0;
    }
    // capture: scroll events of inner containers (e.g. modals) do not bubble
    document.addEventListener('scroll', handlePortalPosition, true);
    window.addEventListener('resize', handlePortalPosition);
    return () => {
      // document.body.style.position = null;
      document.body.style.overflowY = '';
      // document.body.style.inset = null;
      document.removeEventListener('scroll', handlePortalPosition, true);
      window.removeEventListener('resize', handlePortalPosition);
    };
  }, [bindTo, ref.current, fullscreen]);

  return (
    <div id={`in-${props.id}`} className={className} ref={ref}>
      {executer(content || children, props)}
    </div>
  );
});

const Portal: FC<IPortal> = (props: IPortal) => {
  const [id] = useState(props.id || randomId({ prefix: 'snowit_portal' }));
  const externalWidget = document.getElementById('snowit-widget');
  const wrapper = useDomElement({
    id,
    class: 'snowit_portals',
    parent: externalWidget || undefined,
  });

  const { onClose, clickToHide, escToHide } = props;
  const { onUnmount, onMount } = props;
  const outside = useRef(false);
  const container = useRef(null);

  const handleMouseDown = (event) => {
    if (!container.current) {
      outside.current = false;
    } else {
      /**
       * clicking on a portal created within another portal (e.g. a select dropdown inside a modal)
       * would count as clicking outside (because DOM tree with Portals is completely detached)
       * thus selecting an option from a select would close the modal.
       * to prevent this we check every portal created. if a click is within any portal
       * then we could assume we are in the aforementioned case, and we would not close any portal
       * this brings some other quirks, but we can counter those with ui/ux
       */
      const cs = Array.from(document.getElementsByClassName('snowit_portals'));
      const isOutside = cs.every((c) => !c.contains(event.target));
      outside.current = isOutside;
      /** this below is the previous way to detect a click outside */
      // outside.current = !container.current.contains(event.target);
    }
  };

  const handleClick = () => {
    if (outside.current) {
      executer(onClose);
    }
  };

  const handleKeyDown = (event) => {
    if (event.keyCode === 27) {
      executer(onClose);
    }
  };

  useEffect(() => {
    executer(onMount);
    return () => {
      executer(onUnmount);
    };
  }, []);

  useEffect(() => {
    if (clickToHide) {
      document.addEventListener('mousedown', handleMouseDown);
      document.addEventListener('click', handleClick);
    }
    if (escToHide) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      if (clickToHide) {
        document.removeEventListener('mousedown', handleMouseDown);
        document.removeEventListener('click', handleClick);
      }
      if (escToHide) {
        document.removeEventListener('keydown', handleKeyDown);
      }
    };
  }, []);

  if (!wrapper) return null;

  return createPortal(
    // portal content renders outside the app tree, so it needs its own feature context
    <Motion>
      <AnimatePresence>
        {props.mounted && <InnerPortal id={id} ref={container} {...props} />}
      </AnimatePresence>
    </Motion>,
    wrapper,
  ) as ReactElement;
  // return <div>{markup}</div>;
};

export default Portal;
